import { useEffect, useState } from 'react';
import { adminApi, type Posting } from './api/adminClient';
import StatusBadge from './components/StatusBadge';

export default function AdminJobsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<{ page: number; total: number; postings: Posting[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    if (status) params.set('status', status);
    if (source) params.set('source', source);
    if (search) params.set('search', search);
    adminApi
      .listPostings(params)
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e: Error) => setErr(e.message));
  };

  useEffect(load, [page, status, source, search]);

  const archive = async (p: Posting) => {
    await adminApi.patchPosting(p._id, { status: p.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE' });
    load();
  };

  const syncDol = async () => {
    setSyncing(true);
    setErr(null);
    try {
      await adminApi.syncDol();
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Ofertas de empleo</h1>
        <button className="btn btn-secondary btn-sm" onClick={syncDol} disabled={syncing}>
          {syncing ? 'Sincronizando…' : 'Sincronizar DOL'}
        </button>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <div className="flex gap-2 mb-4 flex-wrap">
            <input
              className="input input-bordered input-sm flex-1 min-w-[220px]"
              placeholder="Buscar cargo, empresa, ubicación"
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            />
            <select
              className="select select-bordered select-sm"
              value={status}
              onChange={(e) => { setPage(1); setStatus(e.target.value); }}
            >
              <option value="">Estado</option>
              <option value="ACTIVE">Activas</option>
              <option value="ARCHIVED">Archivadas</option>
            </select>
            <select
              className="select select-bordered select-sm"
              value={source}
              onChange={(e) => { setPage(1); setSource(e.target.value); }}
            >
              <option value="">Fuente</option>
              <option value="dol_h2b">DOL H2B</option>
              <option value="dol_h2a">DOL H2A</option>
              <option value="static_csv">CSV</option>
              <option value="manual">Manual</option>
            </select>
          </div>

          {err && <div className="alert alert-error mb-2">{err}</div>}

          <div className="overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Cargo</th>
                  <th>Empresa</th>
                  <th>Ubicación</th>
                  <th>Fuente</th>
                  <th>Estado</th>
                  <th>Asignados</th>
                  <th>Apps</th>
                  <th>Gate</th>
                  <th>Creada</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data?.postings ?? []).map((p) => (
                  <tr key={p._id}>
                    <td>{p.title}</td>
                    <td>{p.company}</td>
                    <td>{p.location}</td>
                    <td><span className="badge badge-ghost badge-sm">{p.source}</span></td>
                    <td><StatusBadge value={p.status} /></td>
                    <td>{p.assigned_count}</td>
                    <td>{p.application_count}</td>
                    <td>{p.gate_pass_rate !== null ? `${(p.gate_pass_rate * 100).toFixed(0)}%` : '—'}</td>
                    <td className="text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td>
                      <button className="btn btn-ghost btn-xs" onClick={() => archive(p)}>
                        {p.status === 'ACTIVE' ? 'Archivar' : 'Reactivar'}
                      </button>
                    </td>
                  </tr>
                ))}
                {data && data.postings.length === 0 && (
                  <tr><td colSpan={10} className="text-center opacity-60 py-8">Sin ofertas. Sincroniza DOL o crea una manual.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="join mt-4">
            <button className="join-item btn btn-sm" disabled={page === 1} onClick={() => setPage(page - 1)}>«</button>
            <button className="join-item btn btn-sm">Página {data?.page ?? 1}</button>
            <button className="join-item btn btn-sm" disabled={!data || page * 25 >= data.total} onClick={() => setPage(page + 1)}>»</button>
          </div>
        </div>
      </div>
    </div>
  );
}
